import { Inject, Injectable } from "@angular/core";
import {
    IBetStatisticsRecordPayload,
    IMyBetStatisticsRecordPayload,
    IStatisticsRepository
} from "./interfaces";
import { catchError, firstValueFrom, from, Observable, of, switchMap, tap, throwError } from "rxjs";
import { EStatisticsTimespan } from "./domain/enums";
import { Store } from "@karma-games/angular-devkit/src/lib/extended-store";
import { IRoundBet } from "../core/round/interfaces";
import { GAME_NAME_TOKEN } from "@karma-games/angular-devkit/src/lib/interfaces";
import { HttpService } from "@karma-games/angular-devkit/src/lib/services/http";
import { map, take } from "rxjs/operators";
import { CurrencyService } from "../shared/services/currency.service";
import { IPageRequestOptions } from "@karma-games/angular-devkit/src/lib/interfaces";
import {
    addRemoveBetRecords,
    clearBetRecords,
    updateBetRecord,
    updateStatisticsLastPageFlag
} from "./store/statistics.actions";
import { statisticsRecordsPageLength } from "./constants";

@Injectable()
export class StatisticsRepository implements IStatisticsRepository {
    private myBets$: Observable<IRoundBet[]> | undefined = undefined;

    constructor(private store: Store,
                @Inject(GAME_NAME_TOKEN) private gameName: string,
                private http: HttpService,
                private currencyService: CurrencyService) {
    }

    get(): Observable<IRoundBet[]> {
        return this.store.subscribeRoundBets();
    }

    subscribeTotalBetsCount(): Observable<number> {
        return this.store.subscribeTotalBetsCount();
    }

    getMyBets(): Observable<IRoundBet[]> {
        if (!this.myBets$) {
            this.myBets$ = this.store.subscribeMyBetsList();
            const options: IPageRequestOptions = { skip: 0, take: statisticsRecordsPageLength };
            return from(this.fetchMyBets(options))
                .pipe(catchError(e => {
                    return throwError(e);
                }));
        }

        return this.myBets$;
    }

    refetchBetRecords(): void {
        this.store.dispatch(clearBetRecords());
        const options: IPageRequestOptions = { skip: 0, take: statisticsRecordsPageLength };
        this.fetchMyBets(options).pipe(take(1)).subscribe();
    }

    async requestNewMyBetsPage(options: IPageRequestOptions): Promise<IRoundBet[]> {
        try {
            return await firstValueFrom(this.fetchMyBets(options, true));
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    fetchMyBets(options: IPageRequestOptions, newPageRequest: boolean = false): Observable<IRoundBet[]> {
        return this.http.post(`games/${this.gameName}/myBets`, options).pipe(map(response => response.body.data),
            map((records: IMyBetStatisticsRecordPayload[]) => {
                return <IRoundBet[]>records.map(r => {
                    return {
                        id: (r.createdAt + Math.random()).toString(),
                        date: r.createdAt,
                        bet: r.bet,
                        multiplier: r.multiplier,
                        cashout: this.currencyService.convertCurrencyAmount(r.payout, r.currency),
                        totalAmount: this.currencyService.convertCurrencyAmount(r.payout, r.currency),
                        split: r.split
                    };
                });
            }),
            catchError((err) => {
                console.error(err);
                return of([]);
            }),
            tap((records: IRoundBet[]) => {
                this.store.dispatch(addRemoveBetRecords({ bets: records }));
                if (newPageRequest && (!records.length || records.length < options.take)) {
                    this.store.dispatch(updateStatisticsLastPageFlag({ reachedLastPage: true }));
                }
            }),
            switchMap((_records: IRoundBet[]) => {
                return <Observable<IRoundBet[]>>this.myBets$;
            }));
    }

    addRemoveUserRoundBetRecords(roundBetRecords: IRoundBet[]): void {
        this.store.dispatch(addRemoveBetRecords({ bets: roundBetRecords }));
    }

    updateUserRoundBetRecord(round: IRoundBet): void {
        this.store.dispatch(updateBetRecord({ bet: round }));
    }

    getTopBet(modifier: EStatisticsTimespan): Observable<IRoundBet[]> {
        return this.http.get(`games/${this.gameName}/topBets?interval=${modifier}`).pipe(map(response => response.body.data),
            map((records: IBetStatisticsRecordPayload[]) => {
                return <IRoundBet[]>records.map(r => {
                    return {
                        id: r.player!.id ?? `${r.player!.name}_${r.createdAt}`,
                        user: {
                            id: r.player!.id ?? `${r.player!.name}_${r.createdAt}`,
                            name: r.player!.name,
                            currency: r.currency,
                            avatarUrl: r.player?.avatarUrl,
                        },
                        // date: r.timestamp,
                        bet: r.bet,
                        multiplier: r.multiplier,
                        cashout: this.currencyService.convertCurrencyAmount(r.payout, r.currency),
                        totalAmount: this.currencyService.convertCurrencyAmount(r.payout, r.currency),
                        split: r.split
                    };
                });
            }),
            catchError((err) => {
                console.error(err);
                return of([]);
            }));
    }
}
